// TaskRelatedTasks Panel Component
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Text,
  Alert,
  Group,
  Box,
  Collapse,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconCalendar, IconMapPin } from '@tabler/icons-react';

import { FONT_WEIGHT_SEMIBOLD } from '@/constants/ui';
import { useLocalizedDate } from '../../../../hooks/useLocalizedDate';
import { getRelatedTasksProgress } from '../../../../utils/taskHelpers';
import type { RelatedTaskSummary, Task } from '../../../../types';
import { TaskStatus } from '../../../../types';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PillTabs } from '../../../ui/PillTabs';
import type { PillTab } from '../../../ui/PillTabs';
import { ProgressSection } from '../../../ui/ProgressSection';

import styles from './TaskRelatedTasks.module.css';

interface TaskRelatedTasksProps {
  task: Task;
  onViewTask?: (taskId: string, title: string) => void;
  /**
   * Decides whether a related task can be opened (default: all navigable).
   * Public shares pass a predicate checking presence in the share dataset —
   * out-of-share relations stay visible but are not clickable.
   */
  isTaskNavigable?: (relatedTask: RelatedTaskSummary) => boolean;
}

export const TaskRelatedTasks: React.FC<TaskRelatedTasksProps> = ({
  task,
  onViewTask,
  isTaskNavigable,
}) => {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();

  // Status filter state (local UI state)
  type StatusFilterType = 'all' | TaskStatus;
  const [activeStatusFilter, setActiveStatusFilter] =
    useState<StatusFilterType>('all');

  // Collapse state for compact alert (local UI state)
  const [isAlertExpanded, setIsAlertExpanded] = useState<boolean>(false);

  // Track previous tabs to detect "All" tab appearing
  const prevTabsRef = useRef<StatusFilterType[]>([]);

  // Use related task summaries directly from the task (no store subscription needed)
  const relatedTasksAll = task.relatedTasks;

  // Calculate status counts per type
  const statusCounts = useMemo(() => {
    const counts = {
      all: relatedTasksAll.length,
      [TaskStatus.Pending]: 0,
      [TaskStatus.InProgress]: 0,
      [TaskStatus.Delayed]: 0,
      [TaskStatus.Completed]: 0,
      [TaskStatus.Archived]: 0,
    };

    relatedTasksAll.forEach((relatedTask) => {
      counts[relatedTask.status]++;
    });

    return counts;
  }, [relatedTasksAll]);

  // Configure status filter tabs (conditional rendering)
  const statusTabs: PillTab<StatusFilterType>[] = useMemo(() => {
    // Count how many statuses are present
    const statusesPresent = [
      statusCounts[TaskStatus.Pending] > 0,
      statusCounts[TaskStatus.InProgress] > 0,
      statusCounts[TaskStatus.Delayed] > 0,
      statusCounts[TaskStatus.Completed] > 0,
    ].filter(Boolean).length;

    const tabs: PillTab<StatusFilterType>[] = [];

    // If only 1 status exists: show ONLY that status's tab (not "All")
    if (statusesPresent === 1) {
      if (statusCounts[TaskStatus.Pending] > 0) {
        tabs.push({
          value: TaskStatus.Pending,
          labelKey: 'relatedTasks.tabs.pending',
          count: statusCounts[TaskStatus.Pending],
        });
      } else if (statusCounts[TaskStatus.InProgress] > 0) {
        tabs.push({
          value: TaskStatus.InProgress,
          labelKey: 'relatedTasks.tabs.inProgress',
          count: statusCounts[TaskStatus.InProgress],
        });
      } else if (statusCounts[TaskStatus.Delayed] > 0) {
        tabs.push({
          value: TaskStatus.Delayed,
          labelKey: 'relatedTasks.tabs.delayed',
          count: statusCounts[TaskStatus.Delayed],
        });
      } else if (statusCounts[TaskStatus.Completed] > 0) {
        tabs.push({
          value: TaskStatus.Completed,
          labelKey: 'relatedTasks.tabs.completed',
          count: statusCounts[TaskStatus.Completed],
        });
      }
    }
    // If 2+ statuses exist: show "All" + all existing statuses
    else if (statusesPresent > 1) {
      tabs.push({
        value: 'all',
        labelKey: 'relatedTasks.tabs.all',
        count: statusCounts.all,
      });

      if (statusCounts[TaskStatus.Pending] > 0) {
        tabs.push({
          value: TaskStatus.Pending,
          labelKey: 'relatedTasks.tabs.pending',
          count: statusCounts[TaskStatus.Pending],
        });
      }
      if (statusCounts[TaskStatus.InProgress] > 0) {
        tabs.push({
          value: TaskStatus.InProgress,
          labelKey: 'relatedTasks.tabs.inProgress',
          count: statusCounts[TaskStatus.InProgress],
        });
      }
      if (statusCounts[TaskStatus.Delayed] > 0) {
        tabs.push({
          value: TaskStatus.Delayed,
          labelKey: 'relatedTasks.tabs.delayed',
          count: statusCounts[TaskStatus.Delayed],
        });
      }
      if (statusCounts[TaskStatus.Completed] > 0) {
        tabs.push({
          value: TaskStatus.Completed,
          labelKey: 'relatedTasks.tabs.completed',
          count: statusCounts[TaskStatus.Completed],
        });
      }
    }

    return tabs;
  }, [statusCounts]);

  // Smart tab auto-selection when tabs change
  useEffect(() => {
    const availableValues = statusTabs.map((tab) => tab.value);
    const hasAllTab = availableValues.includes('all');
    const hadAllTab = prevTabsRef.current.includes('all');

    // Case 1: "All" tab just appeared (wasn't there before) → switch to "All"
    if (hasAllTab && !hadAllTab) {
      setActiveStatusFilter('all');
    }
    // Case 2: Current filter no longer available → smart fallback
    else if (!availableValues.includes(activeStatusFilter)) {
      // Prefer "All" if available, otherwise first available tab
      const defaultFilter = hasAllTab ? 'all' : statusTabs[0]?.value || 'all';
      setActiveStatusFilter(defaultFilter);
    }

    // Save current tabs for next render
    prevTabsRef.current = availableValues;
  }, [statusTabs, activeStatusFilter]);

  // Filter by active status tab
  const relatedTasks = useMemo(() => {
    if (activeStatusFilter === 'all') return relatedTasksAll;
    return relatedTasksAll.filter(
      (relatedTask) => relatedTask.status === activeStatusFilter
    );
  }, [relatedTasksAll, activeStatusFilter]);

  // Calculate progress
  const progress = useMemo(() => {
    return getRelatedTasksProgress(task);
  }, [task]);

  return (
    <div className={styles.scrollContainer}>
      {/* Fixed Header - Alert + Progress + Tabs (does NOT scroll) */}
      <div className={styles.fixedHeader}>
        {/* Completion requirement alert */}
        {task.requireRelatedTasksCompletion && (
          <Alert
            icon={<IconAlertCircle size={20} />}
            color="orange"
            variant="light"
            classNames={{
              root: styles.compactAlert,
              icon: styles.compactAlertIcon,
            }}
          >
            {/* Compact 1-line view */}
            <div className={styles.compactAlertContent}>
              <Text size="sm" fw={500} className={styles.compactAlertText}>
                {t('relatedTasks.completionRequired')}
              </Text>
              <UnstyledButton
                onClick={() => setIsAlertExpanded(!isAlertExpanded)}
                className={styles.expandButton}
                aria-expanded={isAlertExpanded}
                aria-label={
                  isAlertExpanded
                    ? t('relatedTasks.collapseAlert')
                    : t('relatedTasks.expandAlert')
                }
              >
                <Text size="xs" c="orange" fw={500}>
                  {isAlertExpanded ? '▲ ' : '▼ '}
                  {isAlertExpanded
                    ? t('relatedTasks.collapseAlert')
                    : t('relatedTasks.expandAlert')}
                </Text>
              </UnstyledButton>
            </div>

            {/* Expandable description */}
            <Collapse expanded={isAlertExpanded} transitionDuration={300}>
              <Text size="sm" className={styles.expandedDescription}>
                {t('relatedTasks.completionRequiredDesc')}
              </Text>
            </Collapse>
          </Alert>
        )}

        {/* Overall progress */}
        <ProgressSection
          title={t('relatedTasks.progress')}
          completed={progress.completed}
          total={progress.total}
        />

        {/* Status filter tabs */}
        {statusTabs.length > 0 && (
          <div className={styles.pillTabsWrapper}>
            <PillTabs
              tabs={statusTabs}
              activeTab={activeStatusFilter}
              onTabChange={setActiveStatusFilter}
              variant="compact"
            />
          </div>
        )}
      </div>

      {/* Scrollable Content - ONLY task list scrolls */}
      <Stack gap="sm" className={styles.scrollableList}>
        {relatedTasks.map((relatedTask) => {
          const navigable = isTaskNavigable?.(relatedTask) ?? true;
          return (
            <Box
              key={relatedTask.id}
              className={navigable ? styles.taskCard : styles.taskCardStatic}
              title={
                navigable ? undefined : t('share.public.notIncludedInShare')
              }
              onClick={
                navigable
                  ? () => onViewTask?.(relatedTask.shortId, relatedTask.title)
                  : undefined
              }
            >
              {/* Title and status */}
              <Group justify="space-between" align="flex-start" mb={6}>
                <Text
                  size="sm"
                  fw={FONT_WEIGHT_SEMIBOLD}
                  className={styles.taskTitle}
                >
                  {relatedTask.title}
                </Text>
                <StatusBadge
                  type="taskStatus"
                  value={relatedTask.status}
                  size="sm"
                />
              </Group>

              {/* Meta information - Location and Date in one row */}
              <Group justify="space-between" align="center" wrap="nowrap">
                {/* Location - left aligned */}
                <Group gap={6} wrap="nowrap">
                  <IconMapPin size={14} className={styles.metaIcon} />
                  <Text size="xs" c="dimmed">
                    {relatedTask.location || t('common.noLocation')}
                  </Text>
                </Group>

                {/* Due Date - right aligned */}
                {relatedTask.dueDate && (
                  <Group gap={6} wrap="nowrap">
                    <IconCalendar size={14} className={styles.metaIcon} />
                    <Text size="xs" c="dimmed">
                      {formatDateCompact(relatedTask.dueDate)}
                    </Text>
                  </Group>
                )}
              </Group>
            </Box>
          );
        })}
      </Stack>
    </div>
  );
};
